import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { usePortfolio, usePortfolioAccounts, usePortfolios } from "@/hooks/useData";
import { Sparkline } from "@/components/widgets/StatWidgets";
import { EquityCurveChart } from "@/components/widgets/ChartWidgets";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList, CommandEmpty } from "@/components/ui/command";
import { fmtMoney, fmtPct, pnlClass } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Plus, Landmark, Clock, ShieldCheck, FlaskConical, Loader2, RefreshCw, AlertTriangle, Zap, Search, Check, LineChart, ChevronLeft, FolderInput, MoreVertical, Link2, Unplug } from "lucide-react";
import { PortfolioShareDialog } from "@/components/AccountShareDialog";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

function timeAgo(iso) {
  if (!iso) return "never";
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

function ServerCombobox({ platform, broker, server, onSelect, onServerType }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(server || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/metaapi/servers", { params: { platform, query: query.trim() } })
        .then(({ data }) => setResults(data || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, platform]);

  const grouped = results.reduce((acc, r) => {
    (acc[r.broker] = acc[r.broker] || []).push(r.server);
    return acc;
  }, {});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-left text-sm shadow-sm"
          data-testid="connect-server-combobox-trigger">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className={cn("flex-1 truncate", !server && "text-muted-foreground")}>
            {server || "Search for your broker's server…"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50" />
            <input
              autoFocus
              value={query}
              onChange={(e) => { setQuery(e.target.value); onServerType(e.target.value); }}
              placeholder="Type your broker name, e.g. IC Markets"
              className="flex h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
              data-testid="connect-server-search-input"
            />
          </div>
          <CommandList className="max-h-64">
            {searching && <div className="p-3 text-center text-xs text-muted-foreground">Searching…</div>}
            {!searching && query.trim().length >= 2 && results.length === 0 && (
              <CommandEmpty className="py-4 text-xs">
                No listed servers match. You can still type the exact server name above and use it as-is.
              </CommandEmpty>
            )}
            {!searching && query.trim().length < 2 && (
              <div className="p-3 text-center text-xs text-muted-foreground">Type at least 2 characters to search…</div>
            )}
            {Object.entries(grouped).map(([brokerName, servers]) => (
              <CommandGroup key={brokerName} heading={brokerName}>
                {servers.map((s) => (
                  <CommandItem key={s} value={s} onSelect={() => { onSelect(brokerName, s); setQuery(s); setOpen(false); }}
                    data-testid={`server-option-${s}`}>
                    <Check className={cn("mr-2 h-3.5 w-3.5", server === s ? "opacity-100" : "opacity-0")} />
                    {s}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ConnectDialog({ open, setOpen, onConnected, portfolioId }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: "", platform: "mt4", login: "", password: "", server: "", broker: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post("/accounts/connect", { ...form, portfolio_id: portfolioId }, { timeout: 150000 });
      toast.success(data.message || "Account connected");
      setOpen(false);
      setForm({ name: "", platform: "mt4", login: "", password: "", server: "", broker: "" });
      onConnected();
    } catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Connection failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) setOpen(o); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Connect a trading account</DialogTitle>
          <DialogDescription className="text-xs">
            Use your <strong>investor (read-only) password</strong>. We can see your history, never touch your trades.
            Just three details from your MetaTrader terminal and you're live.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-[hsl(var(--loss))]/30 bg-[hsl(var(--loss))]/8 p-3 text-xs text-[hsl(var(--loss))]" data-testid="connect-error-message">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="My live account" data-testid="connect-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger data-testid="connect-platform-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mt4">MetaTrader 4</SelectItem>
                  <SelectItem value="mt5">MetaTrader 5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Login</Label>
              <Input required value={form.login} onChange={(e) => setForm({ ...form, login: e.target.value })}
                placeholder="12345678" data-testid="connect-login-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Investor password</Label>
            <Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Read-only password" data-testid="connect-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Server</Label>
            <ServerCombobox
              platform={form.platform}
              broker={form.broker}
              server={form.server}
              onServerType={(v) => setForm((f) => ({ ...f, server: v }))}
              onSelect={(broker, server) => setForm((f) => ({ ...f, broker, server }))}
            />
            <p className="text-[11px] text-muted-foreground">
              Search by broker name (e.g. "IC Markets") and pick your exact server. Can't find it? Just type the exact
              server name shown in your MetaTrader terminal.
            </p>
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="connect-mt5-submit-button">
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating with your broker, up to a minute...</>
            ) : (
              "Connect account"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompleteSetupDialog({ account, setAccount, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.post(`/accounts/${account.account_id}/complete-setup`, { password }, { timeout: 150000 });
      toast.success(data.message || "Account connecting");
      setAccount(null);
      setPassword("");
      onDone();
    } catch (err) {
      setError(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Connection failed. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!account} onOpenChange={(o) => { if (!busy && !o) { setAccount(null); setPassword(""); setError(null); } }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Complete live sync setup</DialogTitle>
          <DialogDescription className="text-xs">
            Live sync is now available. Re-enter the <strong>investor (read-only) password</strong> for{" "}
            <strong>{account?.name}</strong> ({account?.server}) to start syncing - we never stored it earlier for your security.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-[hsl(var(--loss))]/30 bg-[hsl(var(--loss))]/8 p-3 text-xs text-[hsl(var(--loss))]" data-testid="complete-setup-error-message">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {error}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Investor password</Label>
            <Input required autoFocus type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Read-only password" data-testid="complete-setup-password-input" />
          </div>
          <Button type="submit" className="w-full" disabled={busy} data-testid="complete-setup-submit-button">
            {busy ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validating with your broker, up to a minute...</>
            ) : (
              "Start live sync"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_BADGE = {
  connecting: { cls: "border-[hsl(var(--info))]/40 text-[hsl(var(--info))]", label: "Connecting", icon: Loader2, spin: true },
  syncing: { cls: "border-[hsl(var(--info))]/40 text-[hsl(var(--info))]", label: "First sync", icon: Loader2, spin: true },
  pending: { cls: "border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]", label: "Awaiting sync", icon: Clock },
  error: { cls: "border-[hsl(var(--loss))]/40 text-[hsl(var(--loss))]", label: "Error", icon: AlertTriangle },
};

export default function AccountsPage() {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const { data: portfolio, refetch: refetchPortfolio } = usePortfolio(portfolioId);
  const { data: rows, isLoading, refetch } = usePortfolioAccounts(portfolioId);
  const { data: portfolios } = usePortfolios();
  const queryClient = useQueryClient();
  const [connectOpen, setConnectOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [completeTarget, setCompleteTarget] = useState(null);
  const isDemoPortfolio = portfolioId === "demo";

  // poll while any account is connecting or syncing
  const hasTransient = (rows || []).some((r) => ["connecting", "syncing"].includes(r.status));
  useEffect(() => {
    if (!hasTransient) return;
    const t = setInterval(() => refetch(), 12000);
    return () => clearInterval(t);
  }, [hasTransient, refetch]);

  const invalidateAll = () => {
    refetch();
    refetchPortfolio();
    queryClient.invalidateQueries({ queryKey: ["accounts"] });
    queryClient.invalidateQueries({ queryKey: ["analytics"] });
    queryClient.invalidateQueries({ queryKey: ["portfolios"] });
  };

  const moveAccount = async (id, targetPortfolioId) => {
    try {
      await api.patch(`/accounts/${id}`, { portfolio_id: targetPortfolioId });
      const target = (portfolios || []).find((p) => p.portfolio_id === targetPortfolioId);
      toast.success(`Moved to ${target?.name || "portfolio"}`);
      invalidateAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move account");
    }
  };

  const syncNow = async (id) => {
    setSyncing((s) => ({ ...s, [id]: true }));
    try {
      await api.post(`/accounts/${id}/sync`);
      toast.success("Sync started. Fresh data in a moment.");
      setTimeout(() => { invalidateAll(); setSyncing((s) => ({ ...s, [id]: false })); }, 8000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start sync");
      setSyncing((s) => ({ ...s, [id]: false }));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:py-8">
        <Link to="/app/accounts" className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          data-testid="back-to-portfolios">
          <ChevronLeft className="h-3.5 w-3.5" /> All portfolios
        </Link>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight md:text-3xl" data-testid="portfolio-title">
              {portfolio?.name || "Portfolio"}
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {portfolio?.description
                || "Live MT4/MT5 accounts in this portfolio sync automatically."}
            </p>
          </div>
          {!isDemoPortfolio && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="portfolio-share-button">
                <Link2 className="mr-1.5 h-4 w-4" /> Share portfolio
              </Button>
              <Button onClick={() => setConnectOpen(true)} data-testid="connect-account-button">
                <Plus className="mr-1.5 h-4 w-4" /> Connect MT4/MT5
              </Button>
            </div>
          )}
        </div>

        {portfolio && portfolio.account_count > 0 && (
          <div className="mt-5 rounded-2xl border bg-card p-4 sm:p-5" data-testid="portfolio-aggregate">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total balance</div>
                  <div className="font-display text-xl font-semibold tabular-nums" data-testid="portfolio-total-balance">
                    {fmtMoney(portfolio.balance, portfolio.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Equity</div>
                  <div className="font-display text-xl font-semibold tabular-nums">
                    {fmtMoney(portfolio.equity, portfolio.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total P/L</div>
                  <div className={cn("font-display text-xl font-semibold tabular-nums", pnlClass(portfolio.total_pl))}
                    data-testid="portfolio-total-pl">
                    {fmtMoney(portfolio.total_pl, portfolio.currency)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">All-time gain</div>
                  <div className={cn("font-display text-xl font-semibold tabular-nums", pnlClass(portfolio.total_gain_pct))}>
                    {fmtPct(portfolio.total_gain_pct, true)}
                  </div>
                </div>
              </div>
              <div className="flex gap-6 text-center">
                <div>
                  <div className="text-xs font-semibold tabular-nums">{portfolio.win_rate}%</div>
                  <div className="text-[10px] text-muted-foreground">Win rate</div>
                </div>
                <div>
                  <div className="text-xs font-semibold tabular-nums">{portfolio.profit_factor}</div>
                  <div className="text-[10px] text-muted-foreground">P. factor</div>
                </div>
                <div>
                  <div className="text-xs font-semibold tabular-nums">{portfolio.total_trades}</div>
                  <div className="text-[10px] text-muted-foreground">Trades</div>
                </div>
              </div>
            </div>
            <div className="mt-4 border-t pt-3">
              <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                Combined equity curve · {portfolio.account_count} account{portfolio.account_count === 1 ? "" : "s"}
              </div>
              <EquityCurveChart data={portfolio} height={180} currency={portfolio.currency} />
            </div>
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading && [...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}
          {(rows || []).map((a) => {
            const sb = STATUS_BADGE[a.status];
            return (
              <div key={a.account_id}
                className="group rounded-2xl border bg-card p-4 shadow-[0_1px_0_rgba(0,0,0,0.04)] transition-shadow duration-150 hover:shadow-[0_10px_30px_rgba(2,6,23,0.10)]"
                data-testid={`account-card-${a.account_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10">
                      <Landmark className="h-4 w-4 text-primary" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold">{a.name}</div>
                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <span>{a.broker || a.server || "\u2014"} · {(a.platform || "").toUpperCase()}</span>
                        {a.account_type && (
                          <span className={cn("inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-semibold uppercase",
                            a.account_type === "real"
                              ? "bg-[hsl(var(--profit))]/10 text-[hsl(var(--profit))]"
                              : "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))]")}
                            data-testid={`account-type-tag-${a.account_id}`}>
                            {a.account_type === "real" ? <ShieldCheck className="h-2.5 w-2.5" /> : <FlaskConical className="h-2.5 w-2.5" />}
                            {a.account_type}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {a.is_demo ? (
                      <Badge variant="secondary" className="text-[10px]">Demo data</Badge>
                    ) : sb ? (
                      <Badge variant="outline" className={cn("gap-1 text-[10px]", sb.cls)} data-testid={`account-status-${a.account_id}`}>
                        <sb.icon className={cn("h-3 w-3", sb.spin && "animate-spin")} /> {sb.label}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="gap-1 text-[10px] text-[hsl(var(--profit))]">
                        <ShieldCheck className="h-3 w-3" /> Live
                      </Badge>
                    )}
                    {!a.is_demo && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            data-testid={`account-menu-${a.account_id}`}>
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                            <FolderInput className="h-3.5 w-3.5" /> Move to portfolio
                          </DropdownMenuLabel>
                          {(portfolios || []).filter((p) => !p.is_demo && p.portfolio_id !== portfolioId).map((p) => (
                            <DropdownMenuItem key={p.portfolio_id} onClick={() => moveAccount(a.account_id, p.portfolio_id)}
                              data-testid={`move-account-${a.account_id}-to-${p.portfolio_id}`}>
                              {p.name}
                            </DropdownMenuItem>
                          ))}
                          {(portfolios || []).filter((p) => !p.is_demo && p.portfolio_id !== portfolioId).length === 0 && (
                            <DropdownMenuItem disabled>No other portfolio yet</DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(a)}
                            data-testid={`remove-account-${a.account_id}`}>
                            <Unplug className="mr-2 h-3.5 w-3.5" /> Disconnect and delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>

                {a.status === "connecting" || a.status === "syncing" ? (
                  <p className="mt-4 rounded-xl bg-surface-2 p-3 text-xs leading-relaxed text-muted-foreground">
                    {a.status === "connecting"
                      ? "Connecting to your broker terminal. This usually takes a couple of minutes."
                      : "Connected. Crunching your trade history for the first time..."}
                  </p>
                ) : a.status === "error" ? (
                  <div className="mt-4 space-y-2">
                    <p className="rounded-xl border border-[hsl(var(--loss))]/25 bg-[hsl(var(--loss))]/8 p-3 text-xs leading-relaxed text-[hsl(var(--loss))]">
                      {a.error_message || "Something went wrong during sync."}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="h-7 flex-1 text-xs" onClick={() => syncNow(a.account_id)}
                        disabled={syncing[a.account_id]} data-testid={`retry-sync-${a.account_id}`}>
                        <RefreshCw className={cn("mr-1.5 h-3 w-3", syncing[a.account_id] && "animate-spin")} /> Retry connection
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => setDeleteTarget(a)} data-testid={`remove-account-error-${a.account_id}`}>
                        <Unplug className="mr-1.5 h-3 w-3" /> Remove
                      </Button>
                    </div>
                  </div>
                ) : a.status === "pending" ? (
                  <div className="mt-4 space-y-2">
                    <p className="rounded-xl bg-surface-2 p-3 text-xs leading-relaxed text-muted-foreground">
                      This account is saved and ready. Live sync is now available - complete setup to start syncing.
                    </p>
                    <Button size="sm" className="h-7 w-full text-xs" onClick={() => setCompleteTarget(a)}
                      data-testid={`complete-setup-${a.account_id}`}>
                      <Zap className="mr-1.5 h-3 w-3" /> Complete setup
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <div className="font-display text-xl font-semibold tabular-nums">{fmtMoney(a.balance, a.currency)}</div>
                        <div className={cn("text-xs font-medium tabular-nums", pnlClass(a.total_gain_pct))}>
                          {fmtPct(a.total_gain_pct, true)} all-time
                        </div>
                      </div>
                      <Sparkline points={a.sparkline} width={110} height={34}
                        className={a.total_gain_pct >= 0 ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]"} />
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 border-t pt-3 text-center">
                      <div>
                        <div className="text-xs font-semibold tabular-nums">{a.win_rate}%</div>
                        <div className="text-[10px] text-muted-foreground">Win rate</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold tabular-nums text-[hsl(var(--loss))]">-{fmtPct(a.max_drawdown)}</div>
                        <div className="text-[10px] text-muted-foreground">Max DD</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold tabular-nums">{a.profit_factor}</div>
                        <div className="text-[10px] text-muted-foreground">P. factor</div>
                      </div>
                    </div>
                    {!a.is_demo && (
                      <div className="mt-3 flex items-center justify-between border-t pt-2.5">
                        <span className="text-[10px] text-muted-foreground">Last synced {timeAgo(a.last_synced_at)}</span>
                        <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={() => syncNow(a.account_id)}
                          disabled={syncing[a.account_id]} data-testid={`sync-now-${a.account_id}`}>
                          <RefreshCw className={cn("h-3 w-3", syncing[a.account_id] && "animate-spin")} /> Sync now
                        </Button>
                      </div>
                    )}
                    {a.strategy && <p className="mt-2 truncate text-[11px] text-muted-foreground/80">{a.strategy}</p>}
                    <Button variant="outline" size="sm" className="mt-3 h-7 w-full gap-1.5 text-xs"
                      onClick={() => navigate(`/app/account/${a.account_id}`)}
                      data-testid={`analytics-toggle-${a.account_id}`}>
                      <LineChart className="h-3.5 w-3.5" />
                      View analytics
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {!isLoading && (rows || []).length === 0 && (
          <div className="mt-6 rounded-2xl border border-dashed bg-surface-2/40 p-10 text-center" data-testid="portfolio-empty-state">
            <p className="font-display text-lg font-semibold">No accounts in this portfolio yet</p>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              Connect an MT4 or MT5 account with your investor (read-only) password and its full history appears here
              within a couple of minutes.
            </p>
            {!isDemoPortfolio && (
              <Button className="mt-4" onClick={() => setConnectOpen(true)} data-testid="empty-connect-account-button">
                <Plus className="mr-1.5 h-4 w-4" /> Connect MT4/MT5
              </Button>
            )}
          </div>
        )}
      </div>

      <ConnectDialog open={connectOpen} setOpen={setConnectOpen} onConnected={invalidateAll}
        portfolioId={portfolioId} />
      <CompleteSetupDialog account={completeTarget} setAccount={setCompleteTarget} onDone={invalidateAll} />
      <PortfolioShareDialog portfolioId={portfolioId} portfolioName={portfolio?.name || "Portfolio"} open={shareOpen} onClose={() => setShareOpen(false)} />
      <DeleteAccountDialog account={deleteTarget} open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onDeleted={invalidateAll} />
    </AppShell>
  );
}
